import React, { useState, useEffect } from 'react';
import { 
  ExclamationTriangleIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import ChatInterface from './ChatInterface';
import RiskDashboard from './RiskDashboard';
import WorkflowList from './WorkflowList';
import api from '../api/client';

export default function Dashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState('chat');
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCustomerInfo();
  }, []);

  const loadCustomerInfo = async () => {
    try {
      const info = await api.getCustomerInfo();
      setCustomerInfo(info);
    } catch (error) {
      console.error('Failed to load customer info:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'chat', name: 'AI Assistant', icon: ChatBubbleLeftRightIcon },
    { id: 'risk', name: 'Risk Dashboard', icon: ChartBarIcon },
    { id: 'workflows', name: 'Assessments', icon: DocumentTextIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-4">
              <img 
                src="/moyer-logo.png" 
                alt="Moyer Scientific Research" 
                className="h-10 w-auto"
              />
              <div className="border-l border-gray-600 pl-4">
                <h1 className="text-lg font-semibold text-white tracking-wide">CISO Advisor</h1>
                {customerInfo && (
                  <p className="text-xs text-gray-400 font-medium">{customerInfo.company_name || 'Loading...'}</p>
                )}
              </div>
            </div>

            {/* Logout */}
            <button
              onClick={onLogout}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors font-medium"
            >
              <span className="text-sm">Sign Out</span>
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-semibold text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            {activeTab === 'chat' && <ChatInterface />}
            {activeTab === 'risk' && <RiskDashboard />}
            {activeTab === 'workflows' && <WorkflowList />}
          </>
        )}
      </main>
    </div>
  );
}
